<template>
  <p-dialog
    :visible="visible"
    :modal="true"
    :closable="false"
    :showHeader="false"
    class="guest-product-modal-dialog"
    @hide="onCancel"
  >
    <div class="guest-modal-wrapper" :class="{ 'dark-theme': themeDarkMode }">
      <!-- Top Right Close Button -->
      <button class="close-btn" @click="onCancel" title="Close">
        <i class="pi pi-times"></i>
      </button>

      <!-- Scrollable Main Container -->
      <div class="guest-modal-body">
        <!-- Monthly/Annual Pill Toggle -->
        <div class="toggle-container">
          <div class="toggle-wrapper">
            <button
              :class="['toggle-btn', { active: billingCycle === 'monthly' }]"
              @click="billingCycle = 'monthly'"
            >
              Monthly
            </button>
            <button
              :class="['toggle-btn', { active: billingCycle === 'annual' }]"
              @click="billingCycle = 'annual'"
            >
              Annual
            </button>
          </div>
        </div>

        <!-- Premium Feature Alert Banner -->
        <div class="premium-feature-banner">
          <div class="banner-content">
            <span class="banner-sparkle">✦</span>
            <span class="banner-text">
              You're trying to access a <strong>premium feature</strong>! Try now
            </span>
            <span class="trial-highlight-pill">
              <span class="pill-sparkle">★</span> FREE FOR 14 DAYS
            </span>
            <!-- <span class="banner-subtext">— Cancel anytime</span> -->
          </div>
        </div>

        <!-- 4 Cards Side-By-Side Grid -->
        <div class="guest-cards-container">
          <div
            v-for="(product, index) in products"
            :key="index"
            class="guest-pricing-card"
          >
            <!-- Currency Dropdown (Top Right of Card) -->
            <div class="card-currency-select">
              <p-dropdown
                v-if="product.id !== 'free'"
                v-model="product.selectedCurrency"
                :options="currencies"
                optionLabel="country"
                placeholder="Select Currency"
                class="currency-dropdown"
              >
                <template #value="slotProps">
                  <div class="country-item" v-if="slotProps.value">
                    <img
                      :src="`https://flagcdn.com/24x18/${slotProps.value.flagCode}.png`"
                      class="flag-icon"
                    />
                    <span>{{ slotProps.value.country }}</span>
                  </div>
                  <span v-else>{{ slotProps.placeholder }}</span>
                </template>
                <template #option="slotProps">
                  <div class="country-item">
                    <img
                      :src="`https://flagcdn.com/24x18/${slotProps.option.flagCode}.png`"
                      class="flag-icon"
                    />
                    <span>{{ slotProps.option.country }}</span>
                  </div>
                </template>
              </p-dropdown>
              <div v-else class="currency-placeholder">&nbsp;</div>
            </div>

            <!-- Title & Subtitle -->
            <h2 class="card-title">{{ product.title }}</h2>
            <p class="card-subtitle">{{ product.subtitle || '&nbsp;' }}</p>

            <!-- Price Display -->
            <div class="price-display">
              <template v-if="product.id === 'free'">
                <span class="amount">$0</span>
              </template>
              <template v-else>
                <span class="currency-symbol">{{
                  product.selectedCurrency.symbol
                }}</span>
                <span class="amount">{{ getPrice(product) }}</span>
                <span class="period"
                  >{{ product.selectedCurrency.name }} / Month</span
                >
              </template>
            </div>

            <!-- Included Features List -->
            <p class="whats-included">What's included:</p>
            <ul class="features-list">
              <li v-for="(feature, fIndex) in product.features" :key="fIndex">
                <i class="pi pi-check red-check"></i>
                <span v-html="feature"></span>
              </li>
            </ul>

            <!-- Card Action Button -->
            <div class="card-actions">
              <button
                type="button"
                class="action-btn cta-red-btn"
                @click="handleSelectPlan(product)"
              >
                {{ product.ctaPrimary }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </p-dialog>
</template>

<script>
import { mapMutations, mapGetters } from 'vuex'

export default {
  name: 'GuestProductModal',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      billingCycle: 'monthly',
      currencies: [
        {
          name: 'USD',
          symbol: '$',
          code: 'USD',
          country: 'United States',
          flagCode: 'us',
        },
        {
          name: 'AUD',
          symbol: '$',
          code: 'AUD',
          country: 'Australia',
          flagCode: 'au',
        },
      ],
      products: [
        {
          id: 'free',
          title: 'FREE',
          subtitle: 'Start with our basic features at no cost.',
          features: [
            'Access Free charts with limited features',
            'End of day data for ASX, NYSE, NASDAQ, SGX and TSX stocks',
          ],
          ctaPrimary: 'Get It Free Now',
          selectedCurrency: {
            name: 'USD',
            symbol: '$',
            code: 'USD',
            country: 'United States',
            flagCode: 'us',
          },
          pricing: {
            USD: { monthly: 0, yearly: 0 },
            AUD: { monthly: 0, yearly: 0 },
          },
        },
        {
          id: 'forum',
          title: 'FORUM ONLY',
          subtitle: 'Access ProTrader Exclusive Forum & Trading Room.',
          features: [
            'Get 14 days free trial.',
            'Full access to our Exclusive Trading Room',
            'Learn from our trading professionals',
            'Access to Breakout Wednesday and Protrader Exclusive video content',
            'Access to our Free Charts',
            'Access to drawing tools, Live Data for all NYSE and NASDAQ Stocks',
            'Intraday charts down to 1-minute bars',
            'Learn with Protrader Academy',
          ],
          ctaPrimary: 'Get 14 Days Free',
          selectedCurrency: {
            name: 'USD',
            symbol: '$',
            code: 'USD',
            country: 'United States',
            flagCode: 'us',
          },
          pricing: {
            USD: { monthly: 15.0, yearly: 15.0, code: 'FORUMONLY' },
            AUD: { monthly: 25.0, yearly: 25.0, code: 'FORUMONLY' },
          },
        },
        {
          id: 'pro_noasx',
          title: 'USA ONLY',
          subtitle: 'ProTrader® with live US stocks only.',
          features: [
            'Get 14 days free trial.',
            'Unlimited indicators',
            'Unlimited access to our proprietary scanners',
            'Live data for all NYSE and NASDAQ stocks <br> &nbsp;&nbsp;&nbsp;• Intraday charts down to 1-minute bars',
            'Full access to our Exclusive Trading Room',
            'Learn with Protrader Academy',
            'Up to 4 charts per tab',
            'Fast full support',
            'Up to 25 years of historical data',
          ],
          ctaPrimary: 'Get 14 Days Free',
          selectedCurrency: {
            name: 'USD',
            symbol: '$',
            code: 'USD',
            country: 'United States',
            flagCode: 'us',
          },
          pricing: {
            USD: { monthly: 19.0, yearly: 17.41, code: 'USAONLY' },
            AUD: { monthly: 25.99, yearly: 23.75, code: 'USAONLY' },
          },
        },
        {
          id: 'pro',
          title: 'PREMIUM',
          subtitle:
            'ProTrader® software was developed by professional traders to allow users to find hot stocks in seconds.',
          features: [
            'Get 14 days free trial.',
            'Unlimited indicators',
            'Unlimited access to our proprietary scanners',
            'Live data for all NYSE and NASDAQ stocks <br> &nbsp;&nbsp;&nbsp;• Intraday charts down to 1-minute bars',
            'End of day data for ASX, SGX and TSX stocks <br> &nbsp;&nbsp;&nbsp;• ASX updates hourly during the trading day',
            'Full access to our Exclusive Trading Room',
            'Learn with Protrader Academy',
            'Up to 4 charts per tab',
            'Fast full support',
            'Up to 25 years of historical data',
          ],
          ctaPrimary: 'Get 14 Days Free',
          selectedCurrency: {
            name: 'USD',
            symbol: '$',
            code: 'USD',
            country: 'United States',
            flagCode: 'us',
          },
          pricing: {
            USD: { monthly: 49.0, yearly: 44.92, code: 'PREMIUM' },
            AUD: { monthly: 69.0, yearly: 63.25, code: 'PREMIUM' },
          },
        },
      ],
    }
  },
  computed: {
    ...mapGetters(['themeDarkMode']),
  },
  methods: {
    ...mapMutations(['SET_RESTRICT_POPUP', 'SET_AUTH_MODAL']),
    onCancel() {
      this.SET_RESTRICT_POPUP(false)
    },
    getPrice(product) {
      const currencyCode = product.selectedCurrency.code
      const priceObj = product.pricing[currencyCode]
      if (!priceObj) return '0'
      return this.billingCycle === 'monthly'
        ? priceObj.monthly
        : priceObj.yearly
    },
    handleSelectPlan(product) {
      // Close guest product modal and open Create Account modal
      // this.SET_RESTRICT_POPUP(false)
      this.SET_AUTH_MODAL(true)

      // Pass selected product & currency query params if router is available
      if (product.id !== 'free') {
        const currency = product.selectedCurrency.code
        const productCode =
          (this.billingCycle === 'monthly' ? 'M_' : 'Y_') +
          (product.pricing[currency]?.code || product.id.toUpperCase())
        this.$router
          .push({
            query: {
              ...this.$route.query,
              currency,
              product: productCode,
            },
          })
          .catch(() => {})
      } else{
        this.$router
          .push({
            query: {},
          })
          .catch(() => {})
      }
    },
  },
}
</script>

<style scoped>
/* Full-Page Dialog Viewport Overrides */
:deep(.p-dialog.guest-product-modal-dialog) {
  width: 100vw !important;
  height: 100vh !important;
  max-width: 100vw !important;
  max-height: 100vh !important;
  margin: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  border: none !important;
  background: #ffffff !important;
}

:deep(.p-dialog.guest-product-modal-dialog .p-dialog-content) {
  padding: 0 !important;
  width: 100% !important;
  height: 100% !important;
  overflow: hidden !important;
  background: transparent !important;
}

/* Modal Wrapper (Light Theme Default) */
.guest-modal-wrapper {
  display: flex;
  flex-direction: column;
  width: 100vw;
  height: 100vh;
  background-color: #ffffff;
  color: #131722;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    Trebuchet MS, Ubuntu, sans-serif;
  box-sizing: border-box;
  position: relative;
}

/* Dark Theme Support */
.guest-modal-wrapper.dark-theme {
  background-color: #0c0d10;
  color: #d1d4dc;
}

.close-btn {
  position: absolute;
  top: 1.5rem;
  right: 2rem;
  z-index: 100;
  background: none;
  border: none;
  font-size: 1.85rem;
  color: inherit;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s, transform 0.15s ease;
}

.close-btn:hover {
  background-color: rgba(128, 128, 128, 0.15);
  transform: scale(1.1);
}

/* Body Content Scrollable */
.guest-modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px 32px 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* Monthly / Annual Pill Toggle */
.toggle-container {
  display: flex;
  justify-content: center;
  margin-bottom: 1.15rem;
  width: 100%;
}

.toggle-wrapper {
  background: #f0f0f0;
  border: 1px solid #e0e3eb;
  border-radius: 50px;
  padding: 4px;
  display: flex;
  gap: 4px;
}

.dark-theme .toggle-wrapper {
  background: #151515;
  border-color: #2a2e39;
}

.toggle-btn {
  background: transparent;
  border: none;
  color: #666666;
  padding: 8px 28px;
  border-radius: 50px;
  cursor: pointer;
  font-weight: 700;
  font-size: 0.95rem;
  transition: all 0.2s ease;
}

.dark-theme .toggle-btn {
  color: #a2a2a2;
}

.toggle-btn.active {
  background: #f20e17;
  color: #ffffff;
  box-shadow: 0 2px 10px rgba(242, 14, 23, 0.35);
}

/* Premium Feature Alert Banner */
.premium-feature-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.85rem;
  padding: 8px 22px;
  border-radius: 50px;
  background: linear-gradient(135deg, rgba(242, 14, 23, 0.05) 0%, rgba(240, 243, 250, 0.7) 100%);
  border: 1px solid rgba(242, 14, 23, 0.22);
  box-shadow: 0 4px 16px -2px rgba(19, 23, 34, 0.06);
  backdrop-filter: blur(8px);
  max-width: 90%;
  box-sizing: border-box;
}

.dark-theme .premium-feature-banner {
  background: linear-gradient(135deg, rgba(242, 14, 23, 0.12) 0%, rgba(25, 28, 36, 0.75) 100%);
  border-color: rgba(242, 14, 23, 0.35);
  box-shadow: 0 4px 20px -2px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.06);
}

.banner-content {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px 12px;
  font-size: 0.95rem;
  color: #131722;
  font-weight: 500;
  text-align: center;
}

.dark-theme .banner-content {
  color: #d1d4dc;
}

.banner-sparkle {
  color: #f20e17;
  font-size: 1.05rem;
  line-height: 1;
}

.dark-theme .banner-sparkle {
  color: #ff3b44;
}

.banner-text strong {
  font-weight: 700;
  color: #f20e17;
}

.dark-theme .banner-text strong {
  color: #ffffff;
}

/* Hero Highlight Pill */
.trial-highlight-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: linear-gradient(135deg, #f20e17 0%, #c40911 100%);
  color: #ffffff !important;
  font-size: 0.82rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  padding: 4px 14px;
  border-radius: 50px;
  box-shadow: 0 3px 12px rgba(242, 14, 23, 0.35);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  white-space: nowrap;
}

.pill-sparkle {
  font-size: 0.75rem;
}

.banner-subtext {
  font-size: 0.86rem;
  color: #787b86;
  white-space: nowrap;
}

.dark-theme .banner-subtext {
  color: #868993;
}

/* 4 Cards Side-by-Side Grid */
.guest-cards-container {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  width: 100%;
  max-width: 1400px;
  box-sizing: border-box;
}

.guest-pricing-card {
  background: #ffffff;
  border: 1px solid #e0e3eb;
  border-radius: 20px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  position: relative;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s, border-color 0.2s, box-shadow 0.2s;
}

.dark-theme .guest-pricing-card {
  background: #151515;
  border-color: #2a2e39;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
}

.guest-pricing-card:hover {
  border-color: #f20e17;
  transform: translateY(-3px);
  box-shadow: 0 8px 30px rgba(242, 14, 23, 0.15);
}

/* Currency Select */
.card-currency-select {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  min-height: 36px;
  margin-bottom: 0.5rem;
}

.currency-placeholder {
  min-height: 36px;
}

.currency-dropdown {
  background: #f8f9fa !important;
  border: 1px solid #d1d4dc !important;
  border-radius: 20px !important;
  height: 32px;
}

.dark-theme .currency-dropdown {
  background: #202020 !important;
  border-color: #363a45 !important;
}

:deep(.currency-dropdown .p-dropdown-label) {
  color: #131722 !important;
  font-size: 0.8rem !important;
  padding: 4px 8px !important;
  font-weight: 600;
  margin: auto;
}

.dark-theme :deep(.currency-dropdown .p-dropdown-label) {
  color: #ffffff !important;
}

.country-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  color: inherit;
}

.flag-icon {
  width: 16px;
  height: 12px;
  border-radius: 2px;
}

/* Title & Prices */
.card-title {
  font-size: 1.75rem;
  font-weight: 800;
  margin: 0 0 0.4rem 0;
  text-align: center;
  text-transform: uppercase;
  color: #131722;
  letter-spacing: 0.5px;
}

.dark-theme .card-title {
  color: #ffffff;
}

.card-subtitle {
  color: #666666;
  font-size: 0.85rem;
  text-align: center;
  margin: 0 0 1rem 0;
  min-height: 36px;
  line-height: 1.35;
}

.dark-theme .card-subtitle {
  color: #a2a2a2;
}

.price-display {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.4rem;
  margin-bottom: 1.25rem;
}

.currency-symbol {
  font-size: 1.5rem;
  font-weight: 700;
  color: #131722;
}

.dark-theme .currency-symbol {
  color: #ffffff;
}

.amount {
  font-size: 3rem;
  font-weight: 900;
  color: #131722;
  line-height: 1;
}

.dark-theme .amount {
  color: #ffffff;
}

.period {
  font-size: 0.85rem;
  color: #666666;
}

.dark-theme .period {
  color: #a2a2a2;
}

.whats-included {
  font-weight: 700;
  margin-bottom: 0.8rem;
  font-size: 0.95rem;
  text-align: center;
  color: #131722;
}

.dark-theme .whats-included {
  color: #ffffff;
}

.features-list {
  list-style: none;
  padding: 0;
  margin: 0 0 1.75rem 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.features-list li {
  font-size: 0.875rem;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: #333333;
  line-height: 1.4;
}

.dark-theme .features-list li {
  color: #d1d4dc;
}

.red-check {
  color: #f20e17;
  font-size: 0.95rem;
  margin-top: 3px;
  flex-shrink: 0;
}

/* CTA Red Button */
.card-actions {
  margin-top: auto;
  width: 100%;
}

.cta-red-btn {
  width: 100%;
  height: 48px;
  background: linear-gradient(135deg, #f20e17 0%, #d00c14 100%);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 15px rgba(242, 14, 23, 0.25);
}

.cta-red-btn:hover {
  background: linear-gradient(135deg, #d00c14 0%, #b71c1c 100%);
  transform: scale(1.02);
  box-shadow: 0 6px 20px rgba(242, 14, 23, 0.45);
}

/* Responsive Grid */
@media screen and (max-width: 1180px) {
  .guest-cards-container {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media screen and (max-width: 680px) {
  .guest-cards-container {
    grid-template-columns: 1fr;
  }
}
</style>
